/**
 * PATH: packages/kanban-host/src/allocation/AllocateChipCardSearch.tsx
 * FILE_ID: KANBANHOST-ALLOCATE-CARD-SEARCH-001
 * PURPOSE: The "link to an existing card" step of the AllocateChip
 *          popover — a debounced typeahead over the caller's visible
 *          Kanban cards. Clicking a result links the open email onto
 *          that card.
 *
 * WHY: Operator (2026-06-24): *"Allow me to allocate a Card which
 *      already exists in Kanban to the email ... with quick search."*
 *      Extracted into its own file so AllocateChip.tsx (already a large
 *      grandfathered file) doesn't grow further — single responsibility:
 *      search input → results list → onPick.
 *
 * FLOW: type → debounce(220ms) → api.searchCards(q) → list → onPick(card)
 *
 * DEPENDENCIES:
 *   - @quotech/ui/kanban : KanbanApi.searchCards + CardSearchResult type
 */

import { useEffect, useRef, useState } from 'react'
import { ChevronRight, Mail, Search } from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'
import type { CardSearchResult, KanbanApi } from '@quotech/ui/kanban'

const MIN_CHARS = 2
const DEBOUNCE_MS = 220

export interface CardSearchListProps {
  api: KanbanApi
  organisationId?: number | null
  /** Linking-in-progress flag from the parent's link hook — disables
   *  result rows so a double-click can't fire two links. */
  busy: boolean
  /** Inline error from the link attempt (e.g. permission). */
  linkError?: string | null
  onPick: (card: CardSearchResult) => void
}

/** Debounced card typeahead. Each query supersedes the previous one via
 *  a monotonically-increasing request id so an out-of-order slow
 *  response can never overwrite a newer result set. */
export function CardSearchList({
  api,
  organisationId,
  busy,
  linkError,
  onPick,
}: CardSearchListProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<CardSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const reqIdRef = useRef(0)

  const trimmed = query.trim()
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_CHARS

  useEffect(() => {
    if (typeof api.searchCards !== 'function') {
      setError('Card search is not available here.')
      return
    }
    if (trimmed.length < MIN_CHARS) {
      setResults([])
      setLoading(false)
      setError(null)
      return
    }
    const reqId = ++reqIdRef.current
    setLoading(true)
    setError(null)
    const timer = setTimeout(async () => {
      try {
        const rows =
          organisationId == null
            ? await api.searchCards!(trimmed)
            : await api.searchCards!(trimmed, { organisationId })
        // Ignore a stale response that resolved after a newer keystroke.
        if (reqId !== reqIdRef.current) return
        setResults(rows)
      } catch {
        if (reqId !== reqIdRef.current) return
        setError('Could not search cards. Try again.')
        setResults([])
      } finally {
        if (reqId === reqIdRef.current) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [api, organisationId, trimmed])

  return (
    <div className="flex flex-col">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
        Link to an existing card
      </div>

      <div className="px-1.5 pb-1">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground/60"
            aria-hidden
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search cards by title…"
            autoFocus
            className="w-full rounded border border-border bg-background pl-6 pr-2 py-1 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label="Search cards"
            data-testid="allocate-card-search-input"
          />
        </div>
      </div>

      <div className="max-h-[220px] overflow-y-auto">
        {tooShort && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            Type at least {MIN_CHARS} characters…
          </div>
        )}
        {!tooShort && loading && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            Searching…
          </div>
        )}
        {!loading && error && (
          <div className="px-2 py-1 text-[11px] text-red-500">{error}</div>
        )}
        {!loading && !error && trimmed.length >= MIN_CHARS && results.length === 0 && (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            No cards match “{trimmed}”.
          </div>
        )}
        {results.map((card) => (
          <button
            key={card.id}
            type="button"
            disabled={busy}
            onClick={() => onPick(card)}
            title={`Link this email to “${card.title}”`}
            className={cn(
              'flex items-start gap-2 w-full px-2 py-1.5 text-left rounded transition-colors',
              'hover:bg-accent disabled:opacity-50',
            )}
            data-testid="allocate-card-search-result"
          >
            <Mail
              className="h-3 w-3 mt-0.5 text-muted-foreground/60 shrink-0"
              aria-hidden
            />
            <span className="flex-1 min-w-0">
              <span className="block truncate text-[11px] text-foreground">
                {card.title}
              </span>
              <span className="block truncate text-[9px] text-muted-foreground/70">
                {card.board_name} · {card.lane_name}
                {card.primary_email_id ? ' · adds to thread' : ''}
              </span>
            </span>
            <ChevronRight
              className="h-3 w-3 mt-0.5 text-muted-foreground/50 shrink-0"
              aria-hidden
            />
          </button>
        ))}
      </div>

      {linkError && (
        <div
          className="border-t border-border mt-1 px-2 py-1 text-[11px] text-red-500"
          role="alert"
          data-testid="allocate-card-link-error"
        >
          {linkError}
        </div>
      )}
    </div>
  )
}
